import * as d3 from 'd3'
import countriesTopo from 'world-atlas/countries-110m.json'
import { feature, mesh } from 'topojson-client'
import { getViewportFeature } from '../charts/geo/pointMapViewport'

const LAND_FEATURE = feature(countriesTopo, countriesTopo.objects.land)
const COUNTRY_BORDERS = mesh(
  countriesTopo,
  countriesTopo.objects.countries,
  (a, b) => a !== b
)

const PROJECTIONS = {
  'Natural Earth': () => d3.geoNaturalEarth1(),
  Mercator: () => d3.geoMercator(),
  'Equal Earth': () => d3.geoEqualEarth(),
}

function getProjection(name) {
  return (PROJECTIONS[name] || PROJECTIONS['Natural Earth'])()
}

function clearCanvas(canvas, width, height, pixelRatioOverride = null) {
  const ctx = canvas.getContext('2d')
  const ratio = pixelRatioOverride || window.devicePixelRatio || 1
  canvas.width = Math.round(width * ratio)
  canvas.height = Math.round(height * ratio)
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
  ctx.clearRect(0, 0, width, height)
  return ctx
}

function buildPreviewColorScale(values, colorScaleOption, fallbackColor) {
  const userScaleValues = Array.isArray(colorScaleOption?.userScaleValues)
    ? colorScaleOption.userScaleValues
    : []
  const defaultColor = colorScaleOption?.defaultColor || fallbackColor

  if (!userScaleValues.length) {
    return () => defaultColor
  }

  const exactMatches = new Map(
    userScaleValues
      .filter((item) => item && item.domain !== undefined && item.range)
      .map((item) => [String(item.domain), item.range])
  )

  const numericEntries = userScaleValues
    .filter(
      (item) =>
        item &&
        item.range &&
        item.domain !== undefined &&
        Number.isFinite(Number(item.domain))
    )
    .map((item) => ({
      domain: Number(item.domain),
      range: item.range,
    }))
    .sort((a, b) => a.domain - b.domain)

  if (numericEntries.length >= 2) {
    const scale = d3
      .scaleLinear()
      .domain(numericEntries.map((item) => item.domain))
      .range(numericEntries.map((item) => item.range))
      .clamp(true)

    return (value) => {
      const numericValue = Number(value)
      if (Number.isFinite(numericValue)) {
        return scale(numericValue)
      }

      return exactMatches.get(String(value)) || defaultColor
    }
  }

  const ordinalDomain = Array.from(
    new Set(values.filter((value) => value !== undefined && value !== null).map(String))
  )

  if (!ordinalDomain.length) {
    return () => defaultColor
  }

  const explicitRange = ordinalDomain.map(
    (domainValue, index) =>
      exactMatches.get(domainValue) ||
      userScaleValues[index % userScaleValues.length]?.range ||
      defaultColor
  )

  const scale = d3.scaleOrdinal().domain(ordinalDomain).range(explicitRange)
  return (value) => scale(String(value))
}

function buildPreviewOpacityScale(values, colorScaleOption, fallbackOpacity = 1) {
  const userScaleValues = Array.isArray(colorScaleOption?.userScaleValues)
    ? colorScaleOption.userScaleValues
    : []
  const defaultOpacity = Number.isFinite(Number(colorScaleOption?.defaultOpacity))
    ? Math.max(0, Math.min(1, Number(colorScaleOption.defaultOpacity)))
    : fallbackOpacity

  if (!userScaleValues.length) {
    return () => defaultOpacity
  }

  const exactMatches = new Map(
    userScaleValues
      .filter((item) => item && item.domain !== undefined)
      .map((item) => {
        const opacity = Number(item.opacity)
        return [
          String(item.domain),
          Number.isFinite(opacity)
            ? Math.max(0, Math.min(1, opacity))
            : defaultOpacity,
        ]
      })
  )

  const numericEntries = userScaleValues
    .filter(
      (item) =>
        item &&
        item.domain !== undefined &&
        Number.isFinite(Number(item.domain)) &&
        Number.isFinite(Number(item.opacity))
    )
    .map((item) => ({
      domain: Number(item.domain),
      opacity: Math.max(0, Math.min(1, Number(item.opacity))),
    }))
    .sort((a, b) => a.domain - b.domain)

  if (numericEntries.length >= 2) {
    const scale = d3
      .scaleLinear()
      .domain(numericEntries.map((item) => item.domain))
      .range(numericEntries.map((item) => item.opacity))
      .clamp(true)

    return (value) => {
      const numericValue = Number(value)
      if (Number.isFinite(numericValue)) {
        return scale(numericValue)
      }
      return exactMatches.get(String(value)) ?? defaultOpacity
    }
  }

  const ordinalDomain = Array.from(
    new Set(values.filter((value) => value !== undefined && value !== null).map(String))
  )

  if (!ordinalDomain.length) {
    return () => defaultOpacity
  }

  const explicitRange = ordinalDomain.map((domainValue, index) => {
    const directMatch = exactMatches.get(domainValue)
    if (directMatch !== undefined) return directMatch
    const fallbackValue = Number(userScaleValues[index % userScaleValues.length]?.opacity)
    return Number.isFinite(fallbackValue)
      ? Math.max(0, Math.min(1, fallbackValue))
      : defaultOpacity
  })

  const scale = d3.scaleOrdinal().domain(ordinalDomain).range(explicitRange)
  return (value) => Number(scale(String(value)))
}

function getScaledLabelFontSize(baseSize, brandTheme) {
  const scale = Number(brandTheme?.labelFontScale ?? 1)
  const safeBase = Number(baseSize)
  if (!Number.isFinite(safeBase)) return `${baseSize}px`

  const nextSize =
    Number.isFinite(scale) && Math.abs(scale - 1) > 0.001
      ? Math.max(1, Math.round(safeBase * scale * 10) / 10)
      : safeBase

  return `${nextSize}px`
}

export function renderPointMapCanvas(
  canvas,
  data,
  visualOptions,
  mapping,
  renderScale = null,
  brandTheme = null
) {
  if (!canvas) return

  const {
    width,
    height,
    background,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    projection: projectionName,
    viewMode,
    pointRadius,
    minPointRadius,
    maxPointRadius,
    pointOpacity,
    pointColor,
    pointStroke,
    pointStrokeWidth,
    showLabels,
    labelFontSize,
    showGraticule,
    landFill,
    countryBorderColor,
    countryBorderWidth,
    colorScale,
  } = visualOptions

  const ctx = clearCanvas(canvas, width, height, renderScale)
  const pointFillScale = buildPreviewColorScale(
    data.map((point) => point.__pointMapColor),
    colorScale,
    pointColor
  )
  const pointOpacityScale = buildPreviewOpacityScale(
    data.map((point) => point.__pointMapColor),
    colorScale,
    1
  )

  ctx.fillStyle = background
  ctx.fillRect(0, 0, width, height)

  const chartWidth = Math.max(0, width - marginLeft - marginRight)
  const chartHeight = Math.max(0, height - marginTop - marginBottom)

  ctx.save()
  ctx.translate(marginLeft, marginTop)

  const projection = getProjection(projectionName)
  projection.fitExtent(
    [
      [0, 0],
      [chartWidth, chartHeight],
    ],
    getViewportFeature(viewMode, data, visualOptions)
  )

  const path = d3.geoPath(projection, ctx)

  ctx.beginPath()
  path(LAND_FEATURE)
  ctx.fillStyle = landFill
  ctx.fill()
  ctx.lineWidth = 0.75
  ctx.strokeStyle = '#d8e1db'
  ctx.stroke()

  ctx.beginPath()
  path(COUNTRY_BORDERS)
  ctx.lineWidth = countryBorderWidth
  ctx.strokeStyle = countryBorderColor
  ctx.stroke()

  if (showGraticule) {
    ctx.beginPath()
    path(d3.geoGraticule10())
    ctx.lineWidth = 0.7
    ctx.strokeStyle = '#e2e7ed'
    ctx.globalAlpha = 0.8
    ctx.stroke()
    ctx.globalAlpha = 1
  }

  const sizeExtent = d3.extent(
    data
      .map((point) => point.__pointMapSize)
      .filter((value) => typeof value === 'number' && Number.isFinite(value))
  )
  const radiusScale =
    sizeExtent[0] !== undefined &&
    sizeExtent[1] !== undefined &&
    sizeExtent[0] !== sizeExtent[1]
      ? d3.scaleSqrt().domain(sizeExtent).range([minPointRadius, maxPointRadius])
      : null

  for (const point of data) {
    const projected = projection([point.__pointMapLongitude, point.__pointMapLatitude])
    if (!projected) continue

    const [x, y] = projected
    const radius =
      radiusScale && typeof point.__pointMapSize === 'number'
        ? radiusScale(point.__pointMapSize)
        : pointRadius

    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.globalAlpha =
      pointOpacity *
      Number(brandTheme?.markOpacity ?? 1) *
      (mapping.color?.value ? pointOpacityScale(point.__pointMapColor) : 1)
    ctx.fillStyle = mapping.color?.value
      ? pointFillScale(point.__pointMapColor)
      : pointColor
    ctx.fill()
    if (pointStrokeWidth > 0) {
      ctx.lineWidth = pointStrokeWidth
      ctx.strokeStyle = pointStroke
      ctx.stroke()
    }
  }

  ctx.globalAlpha = 1

  if (showLabels && data.length <= 2000 && mapping.label?.value) {
    ctx.fillStyle = '#2d3748'
    ctx.font = `${getScaledLabelFontSize(labelFontSize, brandTheme)} Arial, sans-serif`
    for (const point of data) {
      if (!point.__pointMapLabel) continue
      const projected = projection([point.__pointMapLongitude, point.__pointMapLatitude])
      if (!projected) continue
      const [x, y] = projected
      ctx.fillText(String(point.__pointMapLabel), x + 6, y - 6)
    }
  }

  ctx.restore()
}

export function renderBeeswarmCanvas(
  canvas,
  data,
  visualOptions,
  mapping,
  brandTheme,
  renderScale = null
) {
  if (!canvas) return

  const {
    width,
    height,
    background,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    minDiameter,
    maxDiameter,
    nodePadding,
    sortSeriesBy,
    colorScale,
  } = visualOptions

  const ctx = clearCanvas(canvas, width, height, renderScale)
  ctx.fillStyle = background
  ctx.fillRect(0, 0, width, height)
  const nodeFillScale = buildPreviewColorScale(
    data.map((node) => node.color),
    colorScale,
    brandTheme?.secondaryColor || brandTheme?.primaryColor || '#4bc06a'
  )
  const nodeOpacityScale = buildPreviewOpacityScale(
    data.map((node) => node.color),
    colorScale,
    1
  )

  const margin = {
    top: marginTop,
    right: marginRight,
    bottom: marginBottom,
    left: marginLeft,
  }

  const chartWidth = width - margin.left - margin.right
  const chartHeight = height - margin.top - margin.bottom
  const grouped = Array.from(d3.group(data, (d) => d.series))
  const reduced = grouped.reduce((map, d) => {
    map[d[0]] = d3.sum(d[1], (e) => e.size)
    return map
  }, {})

  switch (sortSeriesBy) {
    case 'Total value (descending)':
      grouped.sort((a, b) => d3.descending(reduced[a[0]], reduced[b[0]]))
      break
    case 'Total value (ascending)':
      grouped.sort((a, b) => d3.ascending(reduced[a[0]], reduced[b[0]]))
      break
    case 'Name':
      grouped.sort((a, b) => d3.ascending(a[0], b[0]))
      break
    default:
      break
  }

  const xDomain = d3.extent(data, (d) => d.xValue)
  const xScale =
    data[0]?.xValue instanceof Date
      ? d3.scaleTime().domain(xDomain).nice().range([0, chartWidth])
      : d3.scaleLinear().domain(xDomain).nice().range([0, chartWidth])

  const sizeScale = d3
    .scaleSqrt()
    .domain(d3.extent(data, (d) => d.size))
    .range([minDiameter / 2, maxDiameter / 2])

  const yScale = d3
    .scaleBand()
    .rangeRound([0, chartHeight])
    .domain(grouped.map((d) => d[0]))
    .align(0.5)
    .padding(0)

  const nodes = data.map((d) => ({
    ...d,
    x: xScale(d.xValue),
    y: yScale(d.series) + yScale.bandwidth() / 2,
  }))

  const simulation = d3
    .forceSimulation(nodes)
    .force('x', d3.forceX().x((d) => xScale(d.xValue)))
    .force('y', d3.forceY((d) => yScale(d.series) + yScale.bandwidth() / 2))
    .force(
      'collision',
      d3.forceCollide().radius((d) => sizeScale(d.size) + nodePadding)
    )
    .stop()

  const ticks = Math.ceil(
    Math.log(simulation.alphaMin()) / Math.log(1 - simulation.alphaDecay())
  )
  for (let i = 0; i < ticks; i += 1) simulation.tick()

  ctx.save()
  ctx.translate(margin.left, margin.top)

  const yTickValues = yScale.domain()
  ctx.strokeStyle = '#d8dde3'
  ctx.fillStyle = brandTheme?.textColor || '#181818'
  ctx.font = `${getScaledLabelFontSize(12, brandTheme)} Inter, Arial, sans-serif`

  for (const series of yTickValues) {
    const y = (yScale(series) || 0) + yScale.bandwidth() / 2
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(chartWidth, y)
    ctx.stroke()
    ctx.fillText(String(series ?? ''), 4, Math.max(12, y - 6))
  }

  for (const node of nodes) {
    ctx.beginPath()
    ctx.arc(node.x, node.y, sizeScale(node.size), 0, Math.PI * 2)
    ctx.globalAlpha =
      Number(brandTheme?.markOpacity ?? 1) * nodeOpacityScale(node.color)
    ctx.fillStyle = nodeFillScale(node.color)
    ctx.fill()
  }
  ctx.globalAlpha = 1

  ctx.strokeStyle = '#7d8793'
  ctx.beginPath()
  ctx.moveTo(0, chartHeight)
  ctx.lineTo(chartWidth, chartHeight)
  ctx.stroke()

  const tickValues =
    xScale.ticks && typeof xScale.ticks === 'function' ? xScale.ticks(6) : xScale.domain()
  ctx.fillStyle = brandTheme?.textColor || '#181818'
  ctx.font = `${getScaledLabelFontSize(12, brandTheme)} Inter, Arial, sans-serif`
  ctx.textAlign = 'center'
  tickValues.forEach((tick) => {
    const x = xScale(tick)
    ctx.beginPath()
    ctx.moveTo(x, chartHeight)
    ctx.lineTo(x, chartHeight + 5)
    ctx.stroke()
    const label =
      tick instanceof Date ? d3.timeFormat('%Y-%m-%d')(tick) : d3.format('~s')(tick)
    ctx.fillText(label, x, chartHeight + 18)
  })
  ctx.textAlign = 'left'

  ctx.restore()
}
